import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Skeleton,
  SkeletonText,
  Stack,
  Switch,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, ColorfulBar, Heading, Input } from '~/components';
import { useAuth, useUpdateUser } from '~/hooks';

type UpdateUserFormData = {
  name: string;
  email: string;
};

export const Profile: React.VFC = () => {
  const isLoading = false;
  const { user } = useAuth();
  const updateUser = useUpdateUser();
  const [multifator, setMultifator] = useState(true);

  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();

  const userFormSchema = yup.object().shape({
    name: yup.string().required('Nome obrigatório'),
    email: yup.string().required('E-mail obrigatório').email('E-mail inválido'),
  });

  const { register, handleSubmit, formState, setValue } = useForm({
    resolver: yupResolver(userFormSchema),
  });

  const { errors } = formState;

  useEffect(() => {
    setValue('name', user.name);
    setValue('email', user.email);
    setMultifator(user.multifator);
  }, [user]);

  const handleSave: SubmitHandler<UpdateUserFormData> = async data => {
    const { login, status, accessType, id, type, id_funcao, responsavel } =
      user;
    const { name, email } = data;
    const formData = {
      login,
      status,
      accessType,
      name,
      email,
      id_funcao,
      multifator,
      responsavel,
    };

    updateUser.mutateAsync({ id, type, formData });
  };

  const handleConfirmDialog = (e: any) => {
    setMultifator(e.target.checked);
    if (!e.target.checked && user.multifator) {
      onDialogOpen();
    }
  };

  if (isLoading)
    return (
      <Box px="8" py="4">
        <Skeleton width="30%" height="20px" />
        <Flex
          flexDir="row"
          flexWrap="wrap"
          w="100%"
          py={9}
          gridGap="12"
          style={{
            position: 'relative',
          }}
        >
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="1000px">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="700px" mt="8">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
      </Box>
    );

  return (
    <>
      <Box px="8" py="4">
        <Heading hasBackButton>Configurações da Conta</Heading>
        <Flex
          flexDir="row"
          flexWrap="wrap"
          w="100%"
          py={9}
          gridGap="12"
          justify="center"
        >
          <Stack
            gridGap={4}
            maxWidth="300px"
            width="100%"
            align="center"
            as="form"
            onSubmit={handleSubmit(handleSave)}
          >
            <Avatar name={user.name} size="2xl" mb={4} />
            <Stack width="100%" gridGap={4}>
              <Heading fontSize="md">Dados Pessoais</Heading>
              <Input
                name="name"
                type="text"
                label="Nome"
                register={register}
                error={errors.name}
              />
              <Input
                name="email"
                type="email"
                label="E-mail"
                register={register}
                error={errors.email}
              />
              {user.type === 'Cliente' && (
                <>
                  <Stack gridGap={4}>
                    <FormControl
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <FormLabel htmlFor="status" mb="0" fontWeight="normal">
                        Segurança multifator
                      </FormLabel>
                      <Switch
                        label="Multifator"
                        id="status"
                        size="md"
                        colorScheme="green"
                        isChecked={multifator}
                        onChange={handleConfirmDialog}
                      />
                    </FormControl>
                  </Stack>
                </>
              )}
            </Stack>
            <Button width="100%" type="submit">
              Alterar dados
            </Button>
          </Stack>
        </Flex>
      </Box>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={undefined}
        onClose={onDialogClose}
        isOpen={isDialogOpen}
        isCentered
        closeOnOverlayClick={false}
        size="xl"
      >
        <AlertDialogOverlay />

        <AlertDialogContent width={800}>
          <ColorfulBar />
          <AlertDialogHeader>Atenção</AlertDialogHeader>
          <AlertDialogBody>
            <Text>
              Você está diminuindo o nível de segurança de suas credenciais.
            </Text>
            Confirma a sua escolha?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              onClick={() => {
                setMultifator(user.multifator);
                onDialogClose();
              }}
              variant="secondary"
              width={100}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                onDialogClose();
              }}
              variant="primary"
              width={100}
              ml={3}
            >
              OK
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
